import { usePageMeta } from "@/lib/seo";
import { IMAGES, MODEL_SECTIONS } from "@/content/site";
import { PageHero } from "@/components/site/PageHero";
import { Reveal, SectionHeader, CtaLink, IconTile } from "@/components/site/primitives";
import { EcosystemDiagram } from "@/components/site/EcosystemDiagram";
import { JourneySteps } from "@/components/site/JourneySteps";
import { CtaBand } from "@/components/site/CtaBand";

export default function ModelPage() {
  usePageMeta({
    title: "Our Healthcare Model — Community Centres & Licensed Partners",
    description: "SwastSaathi's proposed ecosystem: community healthcare centres as access and coordination points, with licensed doctors, labs, pharmacies and hospitals.",
    path: "/healthcare-model",
  });
  return (
    <>
      <PageHero
        testId="model-hero"
        eyebrow="Our healthcare model"
        title="One local access point. A network of licensed care."
        intro="The proposed SwastSaathi model places a community healthcare centre at the heart of a partner ecosystem. The centre provides access and coordination — clinical decisions always remain with qualified healthcare professionals."
      >
        <CtaLink to="/partners#enquiry" testId="model-hero-partner-cta">Partner With Us</CtaLink>
        <CtaLink to="#ecosystem" testId="model-hero-diagram-cta" variant="secondary" arrow={false}>View the ecosystem</CtaLink>
      </PageHero>

      <section id="ecosystem" data-testid="model-ecosystem" className="bg-white py-20 lg:py-28">
        <div className="container-x">
          <SectionHeader eyebrow="The proposed ecosystem" title="Patients → Community Centre → Licensed partners." intro="Patients reach a nearby centre. The centre registers, supports and coordinates. Licensed doctors, diagnostic partners, pharmacy partners and referral hospitals provide the clinical care." />
          <div className="mt-16"><EcosystemDiagram /></div>
          <Reveal className="mt-12 grid gap-4 md:grid-cols-2">
            <div className="rounded-2xl bg-mint p-6">
              <p className="font-display font-semibold text-plum">What the community centre does</p>
              <p className="mt-2 text-[15px] leading-relaxed text-slate">Welcomes patients, assists with registration and basic screening, supports teleconsultations, coordinates tests, medicines, follow-ups and referrals.</p>
            </div>
            <div className="rounded-2xl border border-plum/10 p-6">
              <p className="font-display font-semibold text-plum">What it does not do</p>
              <p className="mt-2 text-[15px] leading-relaxed text-slate">It does not diagnose, prescribe or treat independently. These responsibilities stay with appropriately licensed professionals and authorised partners.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section id="patient-journey" data-testid="model-journey" className="bg-mint py-20 lg:py-28">
        <div className="container-x">
          <SectionHeader eyebrow="Patient journey" title="The proposed patient journey" intro="How a patient would move through the model — to be tested and refined during the pilot." />
          <div className="mt-16"><JourneySteps variant="light" /></div>
        </div>
      </section>

      <section data-testid="model-sections" className="bg-white py-20 lg:py-28">
        <div className="container-x grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <SectionHeader eyebrow="How the model works" title="The building blocks of a trusted network." />
              <Reveal delay={0.1} className="mt-10 hidden lg:block">
                <img src={IMAGES.doctorIndianMale} alt="A male Indian doctor in a white coat with a stethoscope, reading a tablet" loading="lazy" data-testid="model-doctor-image" className="aspect-[4/5] w-full rounded-[1.5rem] object-cover object-top" />
              </Reveal>
            </div>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:col-span-8">
            {MODEL_SECTIONS.map((m, i) => (
              <Reveal key={m.id} delay={(i % 2) * 0.07} className={i === MODEL_SECTIONS.length - 1 ? "sm:col-span-2" : ""}>
                <article id={m.id} data-testid={`model-section-${m.id}`} className={`card-soft card-soft-hover h-full p-7 ${i === MODEL_SECTIONS.length - 1 ? "bg-plum-50" : ""}`}>
                  <IconTile icon={m.icon} tone={i % 3 === 1 ? "plum" : "teal"} />
                  <h3 className="mt-6 text-xl font-semibold">{m.title}</h3>
                  <p className="mt-3 leading-relaxed text-slate">{m.body}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand testId="model-cta" title="Help us shape a responsible model of care." body="We welcome conversations with licensed doctors, laboratories, pharmacies, hospitals and community organisations interested in the Bihar pilot." />
    </>
  );
}
